import { useEffect, useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { analyticsApi } from '../../api/admin.js';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#9333ea', '#0ea5e9', '#ea580c'];

export default function Executive() {
  const [data, setData] = useState(null);
  useEffect(() => { analyticsApi.executive().then(setData); }, []);
  if (!data) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="page-header"><h1>Executive Summary</h1></header>

      <div className="kpi-row">
        <Kpi label="Revenue (this month)" value={fmtMoney(data.revenue.thisMonth)} sub={`Target ${fmtMoney(data.revenue.target)} · ${data.revenue.attainment}%`} />
        <Kpi label="Revenue YTD" value={fmtMoney(data.revenue.ytd)} />
        <Kpi label="Cash on hand" value={fmtMoney(data.cashOnHand)} />
        <Kpi label="Open deals" value={fmtMoney(data.openDeals.total)} sub={`${data.openDeals.count} deals`} />
        <Kpi label="Active projects" value={data.projects.active} sub={`${data.projects.completion}% complete`} />
        <Kpi label="SLA breach rate" value={`${data.sla.breachRate}%`} sub={`${data.sla.breached} of ${data.sla.total}`} />
      </div>

      <div className="grid-2">
        <div className="card">
          <h3>Revenue: target vs actual</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={[{ name: 'This month', target: data.revenue.target, actual: data.revenue.thisMonth }]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="target" fill="#94a3b8" />
              <Bar dataKey="actual" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3>Headcount by department</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.headcount} dataKey="count" nameKey="department" outerRadius={80} label>
                {data.headcount.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3>Project completion</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <ProgressDonut percent={data.projects.completion} />
            <div>
              <div className="muted small">Done {data.projects.done} / {data.projects.total} issues</div>
              <div className="muted small">Across {data.projects.active} active projects</div>
            </div>
          </div>
        </div>

        <div className="card">
          <h3>Open deals total</h3>
          <div className="big-stat">{fmtMoney(data.openDeals.total)}</div>
          <p className="muted small">{data.openDeals.count} open deals across the pipeline</p>
        </div>
      </div>
    </section>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="muted small">{sub}</div>}
    </div>
  );
}

function ProgressDonut({ percent = 0 }) {
  const r = 32, c = 2 * Math.PI * r;
  const offset = c * (1 - percent / 100);
  return (
    <svg width="80" height="80" viewBox="0 0 80 80">
      <circle cx="40" cy="40" r={r} stroke="#e2e8f0" strokeWidth="8" fill="none" />
      <circle cx="40" cy="40" r={r} stroke="#2563eb" strokeWidth="8" fill="none" strokeDasharray={c} strokeDashoffset={offset} transform="rotate(-90 40 40)" strokeLinecap="round" />
      <text x="40" y="46" textAnchor="middle" fontSize="16" fontWeight="700">{percent}%</text>
    </svg>
  );
}

function fmtMoney(n) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n || 0));
}
